import { useEffect, useState } from "react";
import { api } from "../../api";
import { Link } from "react-router-dom";
import { Activity, Search, Inbox, CalendarClock, Film, Disc3, Users, Percent, Megaphone, TrendingUp, MessageSquare, Wallet, Scale, Share2, Copy, Check, Send, Radio as RadioIcon, Eye, EyeOff } from "lucide-react";
import StatDetailModal from "../../components/StatDetailModal";
import { toast } from "sonner";

const DEPT_ACCESS = [
  { key: "scouting", label: "Scouting", icon: Search, path: "scouting" },
  { key: "demo_submissions", label: "Demo Submissions", icon: Inbox, path: "demo-submissions" },
  { key: "release_planning", label: "Release Planning", icon: CalendarClock, path: "release-planning" },
  { key: "content_strategy", label: "Content Strategy", icon: Film, path: "content" },
  { key: "released_songs", label: "Released Songs", icon: Disc3, path: "released-songs" },
  { key: "artist_database", label: "Artist Database", icon: Users, path: "artists" },
  { key: "royalty_splits", label: "Royalty Splits", icon: Percent, path: "royalty", adminOnly: true },
  { key: "legal_tracker", label: "Legal Tracker", icon: Scale, path: "legal", adminOnly: true },
  { key: "marketing", label: "Marketing", icon: Megaphone, path: "marketing" },
  { key: "future_strategy", label: "Future Strategy", icon: TrendingUp, path: "future" },
  { key: "feedback", label: "Feedback", icon: MessageSquare, path: "feedback" },
  { key: "finance", label: "Finance", icon: Wallet, path: "finance", adminOnly: true },
];

export default function CommandDeck({ user }) {
  const [stats, setStats] = useState(null);
  const [openStat, setOpenStat] = useState(null);
  // Which admin stat cards are currently revealed (eye icon toggled on). Reset
  // on each mount — amounts are hidden by default.
  const [revealed, setRevealed] = useState(new Set());
  useEffect(() => { api.overview().then(setStats).catch(() => setStats({})); }, []);
  if (!stats) return <div className="muted">Loading…</div>;
  const firstName = (user.name || "").split(" ")[0];
  const net = stats.net_position ?? 0;
  const brand = "var(--violet)";
  const cards = [
    { label: "Releases In Motion", value: String(stats.releases_in_motion ?? 0), k: "releases", color: brand },
    { label: "Artists on Roster", value: String(stats.artists ?? 0), k: "artists", color: brand },
    { label: "Released Songs", value: String(stats.released_songs ?? 0), k: "released", color: brand },
    { label: "Marketing Campaigns", value: String(stats.marketing_campaigns ?? 0), k: "marketing", color: brand },
  ];
  const adminCards = user.role === "admin" ? [
    {
      label: "Net Position",
      value: `${net >= 0 ? "+" : "−"}₹${Math.abs(net).toLocaleString("en-IN")}`,
      k: "net",
      color: net >= 0 ? "var(--mint)" : "var(--danger)",
      hidden: true,  // Shows "••••••" until the eye-reveal is clicked.
    },
    { label: "Pending Approvals", value: String(stats.pending_approvals ?? 0), k: "pending", color: "var(--amber)" },
  ] : [];
  const depts = DEPT_ACCESS.filter(d => !d.adminOnly || user.role === "admin");
  const toggleReveal = (k, ev) => {
    ev.stopPropagation();  // don't trigger the stat-modal open
    setRevealed(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <div className="muted" style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 600 }}>Command Deck</div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
          <h1 style={{
            fontSize: 40, margin: 0,
            background: "linear-gradient(135deg, #f3f0ff 0%, #a78bfa 50%, #7c6cff 100%)",
            WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent",
          }} data-testid="greeting">Hey! {firstName} <span role="img" aria-label="wave">👋</span></h1>
        </div>
        <p className="muted" style={{ marginTop: 8, fontSize: 15, lineHeight: 1.5 }} data-testid="greeting-sub">Let's make some moves today. Here's where things stand across the label.</p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 28 }}>
        {[...cards, ...adminCards].map(c => {
          const isHidden = c.hidden && !revealed.has(c.k);
          return (
          <button
            key={c.k}
            onClick={() => setOpenStat(c.k)}
            data-testid={`stat-${c.k}`}
            className="panel clickable"
            style={{ padding: 20, textAlign: "left", background: "var(--panel)", cursor: "pointer", border: "1px solid var(--border)", position: "relative" }}
          >
            <div className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em" }}>{c.label}</div>
            <div
              style={{
                fontFamily: "Space Grotesk", fontWeight: 700, fontSize: 34, marginTop: 8,
                color: isHidden ? "var(--muted)" : c.color,
                letterSpacing: isHidden ? "0.1em" : "normal",
              }}
              data-testid={`stat-value-${c.k}`}
            >
              {isHidden ? "••••••" : c.value}
            </div>
            {c.hidden && (
              <button
                type="button"
                onClick={(ev) => toggleReveal(c.k, ev)}
                data-testid={`stat-reveal-${c.k}`}
                aria-label={isHidden ? "Reveal amount" : "Hide amount"}
                title={isHidden ? "Reveal" : "Hide"}
                style={{
                  position: "absolute", top: 14, right: 14,
                  background: "var(--panel2)", border: "1px solid var(--border)",
                  borderRadius: 8, padding: "5px 7px", cursor: "pointer",
                  color: "var(--muted)", display: "inline-flex", alignItems: "center",
                }}
              >
                {isHidden ? <Eye size={13} /> : <EyeOff size={13} />}
              </button>
            )}
          </button>
          );
        })}
      </div>

      {openStat && <StatDetailModal statKey={openStat} onClose={() => setOpenStat(null)} />}

      <div className="muted" style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 12 }}>Department Access</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12 }}>
        {depts.map(d => {
          const Icon = d.icon;
          return (
            <Link key={d.key} to={`/dashboard/${d.path}`} className="panel clickable" style={{ padding: 16, textDecoration: "none", color: "inherit", display: "flex", alignItems: "center", justifyContent: "space-between" }} data-testid={`dept-${d.key}`}>
              <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Icon size={16} style={{ color: "var(--mint)" }} />
                <span style={{ fontSize: 14, fontWeight: 500 }}>{d.label}</span>
              </span>
              <span className="muted" style={{ fontSize: 16 }}>›</span>
            </Link>
          );
        })}
      </div>

      <PublicLinks />
    </div>
  );
}

function PublicLinks() {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const links = [
    {
      key: "submit-demo",
      label: "Public Demo Submission",
      desc: "Share this with producers so they can submit demos without signing in — every submission lands in Demo Submissions.",
      path: "/submit-demo",
      icon: Send,
      color: "var(--mint)",
    },
    {
      key: "pitch",
      label: "Public Pitch / Showcase",
      desc: "Send this to labels, A&R, or anyone — a read-only page showing every released song and featured demo. Never exposes internal notes.",
      path: "/pitch",
      icon: RadioIcon,
      color: "var(--violet)",
    },
  ];
  const [copiedKey, setCopiedKey] = useState(null);
  const copy = async (path, key) => {
    const url = `${origin}${path}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedKey(key);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopiedKey(null), 1800);
    } catch {
      toast.error("Could not copy — long-press or select the link manually");
    }
  };
  return (
    <div style={{ marginTop: 30 }}>
      <div className="muted" style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 12, display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 600 }}>
        <Share2 size={11} /> Shareable public links
      </div>
      <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))" }}>
        {links.map(l => {
          const Icon = l.icon;
          const url = `${origin}${l.path}`;
          const copied = copiedKey === l.key;
          return (
            <div key={l.key} className="panel" data-testid={`public-link-${l.key}`} style={{ padding: 18 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <Icon size={16} style={{ color: l.color }} />
                <div style={{ fontSize: 14, fontWeight: 700 }}>{l.label}</div>
              </div>
              <p className="muted" style={{ fontSize: 12, lineHeight: 1.55, marginBottom: 12 }}>{l.desc}</p>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <code
                  data-testid={`public-link-url-${l.key}`}
                  style={{
                    flex: 1, fontSize: 11, padding: "8px 10px", borderRadius: 8,
                    background: "var(--panel2)", border: "1px solid var(--border)",
                    color: "var(--text-dim)", overflow: "hidden", textOverflow: "ellipsis",
                    whiteSpace: "nowrap", fontFamily: "monospace",
                  }}
                  title={url}
                >
                  {url}
                </code>
                <button
                  className="btn"
                  onClick={() => copy(l.path, l.key)}
                  data-testid={`copy-link-${l.key}`}
                  title="Copy link"
                  style={{ padding: "8px 10px", flexShrink: 0 }}
                >
                  {copied ? <Check size={13} style={{ color: "var(--mint)" }} /> : <Copy size={13} />}
                </button>
                <a
                  href={l.path}
                  target="_blank"
                  rel="noreferrer"
                  className="btn"
                  data-testid={`open-link-${l.key}`}
                  title="Open in new tab"
                  style={{ padding: "8px 10px", flexShrink: 0, display: "inline-flex", alignItems: "center" }}
                >
                  Preview
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
